import React, { useMemo, useState } from 'react';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import { RunningToolTitle } from '@cotera/client/v0/chat/running-tool-title';
import type { ToolRendererProps } from './index';
import { Favicon } from './web-favicon';
import { ToolErrorRow, toolErrorText } from './tool-error-row';
import {
  byteLengthOf,
  cleanString,
  formatBytes,
  getHostname,
  getSiteLabel,
} from './web-tool-renderer-utils';

type SiteScraperInput = {
  url?: string | null;
};

type SiteScraperOutput = {
  markdown?: string | null;
  title?: string | null;
};

/**
 * Renderer for the `cotera/siteScraper/scrape` tool. Mirrors the web-search
 * renderer: a shimmering status line while fetching, then a white result card
 * with the page favicon, title, host, and the amount of data scraped. The
 * scraped page content itself is intentionally not rendered.
 */
export const SiteScraperToolRenderer: React.FC<ToolRendererProps> = ({
  part,
}) => {
  const input = part.input as SiteScraperInput | undefined;
  const url = useMemo(() => cleanString(input?.url), [input]);
  const site = useMemo(() => getSiteLabel(url), [url]);

  if (part.isIncomplete()) {
    return (
      <div className="flex items-center gap-2 py-1 text-sm text-muted-foreground">
        <Favicon url={url} className="h-4 w-4 shrink-0 rounded-sm" />
        <RunningToolTitle className="italic">
          {site !== undefined ? `Analyzing ${site}` : 'Analyzing page'}
        </RunningToolTitle>
      </div>
    );
  }

  if (part.isError()) {
    return (
      <ToolErrorRow
        title={
          site !== undefined
            ? `Analyzing ${site} encountered an error`
            : 'Analyzing page encountered an error'
        }
        errorText={toolErrorText(part)}
        input={part.input}
        icon={<Favicon url={url} className="h-4 w-4 shrink-0 rounded-sm" />}
      />
    );
  }

  const output = part.getOutput<SiteScraperOutput>();
  const markdown = cleanString(output?.markdown);

  return (
    <SiteScraperResult
      url={url}
      site={site}
      title={cleanString(output?.title)}
      sizeLabel={
        markdown !== undefined ? formatBytes(byteLengthOf(markdown)) : undefined
      }
    />
  );
};

const SiteScraperResult: React.FC<{
  url: string | undefined;
  site: string | undefined;
  title: string | undefined;
  sizeLabel: string | undefined;
}> = ({ url, site, title, sizeLabel }) => {
  const [expanded, setExpanded] = useState(false);

  const heading = title ?? site ?? 'Page';
  const host = getHostname(url) ?? site;

  return (
    <div className="flex flex-col gap-2 py-1 text-sm">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
        className="group flex w-full items-center justify-between gap-4 text-left text-muted-foreground hover:text-foreground focus:outline-none"
      >
        <span className="flex min-w-0 items-center gap-2">
          <Favicon url={url} className="h-4 w-4 shrink-0 rounded-sm" />
          <CheckIcon className="h-4 w-4 shrink-0 text-success" />
          <span className="truncate italic">
            {site !== undefined ? `Analyzed ${site}` : 'Analyzed page'}
          </span>
          {sizeLabel !== undefined ? (
            <span className="shrink-0 text-xs text-muted-foreground/80">
              · found {sizeLabel} of data
            </span>
          ) : null}
        </span>
        <ChevronDownIcon
          className={cn(
            'h-4 w-4 shrink-0 transition-transform',
            expanded ? 'rotate-180' : 'rotate-0'
          )}
        />
      </button>

      {expanded ? (
        <div className="rounded-lg border border-border bg-card p-3">
          <div className="flex items-start gap-2.5">
            <Favicon url={url} className="mt-0.5 h-4 w-4 shrink-0 rounded-sm" />
            <div className="min-w-0 flex-1 space-y-0.5">
              {url !== undefined ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="line-clamp-2 font-medium text-foreground hover:underline"
                >
                  {heading}
                </a>
              ) : (
                <div className="line-clamp-2 font-medium text-foreground">
                  {heading}
                </div>
              )}
              {host !== undefined ? (
                <div className="truncate text-xs text-muted-foreground">
                  {host}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
